/**
 * PAGE: TARIFS (Pricing)
 * Page sobre et élégante adaptée au thème light/dark
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import PublicLayout from '../components/layout/PublicLayout';

export default function Pricing() {
 const navigate = useNavigate();

 // FAQ Schema for rich results
 const faqSchema = {
 '@context': 'https://schema.org',
 '@type': 'FAQPage',
 mainEntity: [
 {
 '@type': 'Question',
 name: 'Combien coûte Clairify pour une PME ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: 'Clairify propose 3 plans : Pro à 29€/mois (960K tokens), Business à 79€/mois (2.6M tokens) et Enterprise à 199€/mois (6.6M tokens). Un plan gratuit avec 50 000 tokens/mois est également disponible.',
 },
 },
 {
 '@type': 'Question',
 name: 'C\'est quoi le rollover +20% de tokens ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: '20% de vos tokens non utilisés chaque mois sont automatiquement conservés pour le mois suivant. Votre balance ne repart jamais de zéro, vous ne perdez plus de tokens.',
 },
 },
 {
 '@type': 'Question',
 name: 'Comment fonctionnent les tokens Clairify ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: 'Les tokens sont consommés à chaque interaction avec l\'IA : un message simple consomme environ 8-10K tokens, un Deep Report complet 20-30K tokens. Plus l\'analyse est profonde, plus elle consomme de tokens.',
 },
 },
 {
 '@type': 'Question',
 name: 'Puis-je changer de plan Clairify à tout moment ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: 'Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements sont effectifs immédiatement et vous ne payez que la différence au prorata.',
 },
 },
 {
 '@type': 'Question',
 name: 'Clairify est-il conforme RGPD ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: 'Oui, Clairify est 100% conforme RGPD. Vos données sont hébergées en France sur Scaleway (datacenter Paris-1), avec chiffrement AES-256, et ne sont jamais partagées avec des tiers.',
 },
 },
 {
 '@type': 'Question',
 name: 'Quelle est la différence entre Clairify et Power BI ou Tableau ?',
 acceptedAnswer: {
 '@type': 'Answer',
 text: 'Contrairement à Power BI ou Tableau qui montrent des dashboards statiques, Clairify utilise une IA à mémoire persistante qui apprend de vos données, détecte des patterns récurrents, identifie des contradictions invisibles (Pattern Collisions) et propose des actions concrètes.',
 },
 },
 ],
 };

 const plans = [
 {
 name: 'Pro',
 price: 29,
 period: '/mois',
 tokens: '960 000',
 description: 'Pour les professionnels exigeants',
 features: [
 '960K tokens/mois',
 'Deep Reports stratégiques',
 'Pattern Detection avancée',
 'Collisions Pattern',
 'Agent Analysis',
 'DNA Score',
 'Intégration Notion',
 'Rollover +20%',
 ],
 cta: 'Démarrer Pro',
 highlighted: true,
 badge: 'Populaire',
 },
 {
 name: 'Business',
 price: 79,
 period: '/mois',
 tokens: '2 600 000',
 description: 'Analyses avancées pour équipes',
 features: [
 '2.6M tokens/mois',
 'Deep Reports illimités',
 'Pattern Detection & Memory',
 'Collisions Pattern',
 'Agent Analysis',
 'DNA Score',
 'Support prioritaire',
 'Rollover +20%',
 'Intégration Notion',
 ],
 cta: 'Passer à Business',
 highlighted: false,
 },
 {
 name: 'Enterprise',
 price: 199,
 period: '/mois',
 tokens: '6 600 000',
 description: 'Solutions sur-mesure pour grandes entreprises',
 features: [
 '6.6M tokens/mois',
 'Multi-utilisateurs',
 'Collisions Pattern',
 'Agent Analysis',
 'DNA Score',
 'Webhooks personnalisés',
 'Support 24/7 dédié',
 'Rollover +20%',
 'Intégration Notion',
 ],
 cta: 'Contacter les ventes',
 highlighted: false,
 },
 ];

 return (
 <PublicLayout>
 <SEOHead
 title="Tarifs Clairify — IA Business Intelligence PME | À partir de 29€/mois"
 description="Découvrez les tarifs Clairify : Pro 29€/mois, Business 79€/mois, Enterprise 199€/mois. Plan gratuit disponible. IA analyse données pour PME. Rollover tokens. Hébergé en France."
 keywords={['tarif IA PME', 'prix intelligence artificielle entreprise', 'BI PME pas cher', 'outil IA analyse données prix', 'Clairify tarifs', 'alternative Power BI prix', 'IA business intelligence tarif']}
 structuredData={[faqSchema]}
 />

 <section className="pub-hero">
 <p className="home-kicker">Tarifs</p>
 <h1>Simple. Transparent.</h1>
 <p>Choisissez le plan. Rollover automatique. Sans carte pour commencer.</p>
 </section>

 <section className="pub-wrap">
 <div className="grid md:grid-cols-3 gap-0">
 {plans.map((plan) => (
 <article
 key={plan.name}
 className="border-t border-theme py-8 md:px-8 md:border-t-0 md:border-l first:border-l-0 first:pl-0"
 >
 {plan.badge && (
 <p className="home-kicker mb-4">{plan.badge}</p>
 )}
 <h3 className="font-display text-3xl mb-2">{plan.name}</h3>
 <p className="text-sm text-theme-secondary font-light mb-6">{plan.description}</p>
 <p className="font-display text-5xl mb-1">{plan.price}€<span className="text-lg text-theme-tertiary"> /mois</span></p>
 <p className="text-sm text-[var(--spark-gold)] mb-8">{plan.tokens} tokens/mois</p>
 <ul className="space-y-2 mb-8">
 {plan.features.map((feature) => (
 <li key={feature} className="flex items-start gap-2 text-sm text-theme-secondary font-light">
 <Check size={16} className="mt-0.5 flex-shrink-0 text-[var(--spark-gold)]" />
 {feature}
 </li>
 ))}
 </ul>
 <Button
 onClick={() => navigate('/billing')}
 variant={plan.highlighted ? 'default' : 'outline'}
 className="w-full"
 >
 {plan.cta}
 </Button>
 </article>
 ))}
 </div>

 <div className="mt-20 max-w-2xl">
 <p className="home-kicker mb-4">Questions</p>
 <h2 className="font-display text-3xl mb-8">Rollover, tokens, changement de plan.</h2>
 <div className="space-y-8">
 <div>
 <h3 className="font-medium mb-2">C'est quoi le rollover +20% ?</h3>
 <p className="text-theme-secondary font-light leading-relaxed">
 20% de vos tokens non utilisés chaque mois sont conservés. Votre balance ne repart jamais de zéro.
 </p>
 </div>
 <div>
 <h3 className="font-medium mb-2">Comment fonctionnent les tokens ?</h3>
 <p className="text-theme-secondary font-light leading-relaxed">
 Un message simple : environ 8–10K tokens. Un rapport complet : 20–30K.
 </p>
 </div>
 <div>
 <h3 className="font-medium mb-2">Puis-je changer de plan ?</h3>
 <p className="text-theme-secondary font-light leading-relaxed">
 Oui, à tout moment. Prorata immédiat.
 </p>
 </div>
 </div>
 </div>
 </section>
 </PublicLayout>
 );
}
